import { Avatar, type AvatarSize, type AvatarStatus } from '@trustoo/components/Avatar/v2'
import { AvatarGroup } from '@trustoo/components/AvatarGroup/v2'
import {
    AvatarLabelGroup,
    type AvatarLabelGroupSize,
} from '@trustoo/components/AvatarLabelGroup/v2'
import {
    AvatarProfilePhoto,
    type AvatarProfilePhotoSize,
} from '@trustoo/components/AvatarProfilePhoto/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

// ---------- Avatar family (v2) showcase ----------
// Shared demo people for the avatar showcases. `img` indexes i.pravatar.cc so
// the showcase renders real photos without bundling assets.
const AVATAR_PEOPLE = [
    { src: 'https://i.pravatar.cc/150?img=1', alt: 'Olivia Rhye', initials: 'OR' },
    { src: 'https://i.pravatar.cc/150?img=12', alt: 'Phoenix Baker', initials: 'PB' },
    { src: 'https://i.pravatar.cc/150?img=5', alt: 'Lana Steiner', initials: 'LS' },
    { src: 'https://i.pravatar.cc/150?img=8', alt: 'Demi Wilkinson', initials: 'DW' },
    { src: 'https://i.pravatar.cc/150?img=15', alt: 'Candice Wu', initials: 'CW' },
    { src: 'https://i.pravatar.cc/150?img=33', alt: 'Natali Craig', initials: 'NC' },
    { src: 'https://i.pravatar.cc/150?img=47', alt: 'Drew Cano', initials: 'DC' },
]

const AVATAR_USAGE = `import { Avatar } from '@trustoo/components/Avatar/v2'

// Photo (falls back to initials, then a placeholder icon, on error)
<Avatar src="/olivia.jpg" alt="Olivia Rhye" initials="OR" />

// Initials only / placeholder only
<Avatar initials="OR" />
<Avatar />

// Status overlay + size
<Avatar src="/olivia.jpg" alt="Olivia Rhye" status="online" size="lg" />
<Avatar initials="OR" status="verified" size="xl" />
<Avatar src="/acme.jpg" status="company" companySrc="/acme-logo.jpg" />`

const AVATAR_GROUP_USAGE = `import { AvatarGroup } from '@trustoo/components/AvatarGroup/v2'

<AvatarGroup items={people} size="md" />

// Cap visible coins -> '+N' overflow chip
<AvatarGroup items={people} max={5} />

// Trailing dashed add button
<AvatarGroup items={people} max={5} addMoreButton onAddMoreClick={invite} />`

const AVATAR_LABEL_GROUP_USAGE = `import { AvatarLabelGroup } from '@trustoo/components/AvatarLabelGroup/v2'

<AvatarLabelGroup
  size="md"
  title="Olivia Rhye"
  supportingText="olivia@untitledui.com"
  src="/olivia.jpg"
  status="verified"
/>`

const AVATAR_PROFILE_PHOTO_USAGE = `import { AvatarProfilePhoto } from '@trustoo/components/AvatarProfilePhoto/v2'

<AvatarProfilePhoto size="md" src="/olivia.jpg" alt="Olivia Rhye" verified />

// Initials / placeholder fallback
<AvatarProfilePhoto size="lg" initials="OR" />
<AvatarProfilePhoto size="sm" />`

export const AvatarShowcase: React.FC = () => (
    <Section
        title="Avatar (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Avatar</span> set
                (ComponentSet <InlineCode>5:2390</InlineCode>) — the atomic circular avatar and the
                shared base of the family. Content falls back{' '}
                <InlineCode>image → initials → placeholder</InlineCode>; an optional status glyph
                overlays the bottom-right. Import v2 via{' '}
                <InlineCode>@trustoo/components/Avatar/v2</InlineCode> (net-new — no v1 barrel yet).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes</h3>
                <div className="flex flex-wrap items-end gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['xs', 'sm', 'md', 'lg', 'xl', '2xl'] as AvatarSize[]).map((size) => (
                        <div key={size} className="flex flex-col items-center gap-spacing-md">
                            <Avatar
                                src={AVATAR_PEOPLE[0].src}
                                alt={AVATAR_PEOPLE[0].alt}
                                size={size}
                            />
                            <span className="text-xs font-medium uppercase text-text-tertiary">
                                {size}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Status overlays</h3>
                <div className="flex flex-wrap items-end gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['none', 'online', 'company', 'verified'] as AvatarStatus[]).map((status) => (
                        <div key={status} className="flex flex-col items-center gap-spacing-md">
                            <Avatar
                                src={AVATAR_PEOPLE[2].src}
                                alt={AVATAR_PEOPLE[2].alt}
                                status={status}
                                size="xl"
                            />
                            <span className="text-xs font-medium uppercase text-text-tertiary">
                                {status}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Content fallback</h3>
                <div className="flex flex-wrap items-end gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <div className="flex flex-col items-center gap-spacing-md">
                        <Avatar src={AVATAR_PEOPLE[4].src} alt={AVATAR_PEOPLE[4].alt} size="xl" />
                        <span className="text-xs font-medium uppercase text-text-tertiary">
                            image
                        </span>
                    </div>
                    <div className="flex flex-col items-center gap-spacing-md">
                        <Avatar initials="OR" size="xl" />
                        <span className="text-xs font-medium uppercase text-text-tertiary">
                            initials
                        </span>
                    </div>
                    <div className="flex flex-col items-center gap-spacing-md">
                        <Avatar size="xl" />
                        <span className="text-xs font-medium uppercase text-text-tertiary">
                            placeholder
                        </span>
                    </div>
                    <div className="flex flex-col items-center gap-spacing-md">
                        <Avatar src="https://invalid.example/none.jpg" initials="OR" size="xl" />
                        <span className="text-xs font-medium uppercase text-text-tertiary">
                            broken → initials
                        </span>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={AVATAR_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>size</InlineCode> (xs · sm · md · lg · xl · 2xl),{' '}
                    <InlineCode>src</InlineCode> / <InlineCode>alt</InlineCode>,{' '}
                    <InlineCode>initials</InlineCode>, <InlineCode>status</InlineCode> (none ·
                    online · company · verified) + <InlineCode>companySrc</InlineCode>, and{' '}
                    <InlineCode>ring</InlineCode> (white halo for stacking). A broken image
                    automatically degrades to initials → placeholder.
                </p>
            </div>
        </div>
    </Section>
)

export const AvatarGroupShowcase: React.FC = () => (
    <Section
        title="AvatarGroup (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Avatar group</span> set
                (ComponentSet <InlineCode>5:2326</InlineCode>). A row of overlapping{' '}
                <InlineCode>Avatar</InlineCode> coins with a <InlineCode>+N</InlineCode> overflow
                chip and an optional dashed add button.
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes</h3>
                <div className="flex flex-col gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['xs', 'sm', 'md'] as const).map((size) => (
                        <div key={size} className="flex items-center gap-spacing-lg">
                            <span className="w-8 text-xs font-medium uppercase text-text-tertiary">
                                {size}
                            </span>
                            <AvatarGroup items={AVATAR_PEOPLE} size={size} max={5} />
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Overflow + add button</h3>
                <div className="flex flex-col gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <AvatarGroup items={AVATAR_PEOPLE} size="md" />
                    <AvatarGroup items={AVATAR_PEOPLE} size="md" max={4} />
                    <AvatarGroup items={AVATAR_PEOPLE} size="md" max={4} addMoreButton />
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={AVATAR_GROUP_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>items</InlineCode> ({'{'} src, alt, initials {'}'}[]),{' '}
                    <InlineCode>size</InlineCode> (xs · sm · md), <InlineCode>max</InlineCode>{' '}
                    (overflow → +N chip), and <InlineCode>addMoreButton</InlineCode> /{' '}
                    <InlineCode>onAddMoreClick</InlineCode>.
                </p>
            </div>
        </div>
    </Section>
)

export const AvatarLabelGroupShowcase: React.FC = () => (
    <Section
        title="AvatarLabelGroup (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Avatar label group</span> set
                (ComponentSet <InlineCode>5:2244</InlineCode>). An <InlineCode>Avatar</InlineCode>{' '}
                next to a bold title over muted supporting text.
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes</h3>
                <div className="flex flex-col gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['sm', 'md', 'lg', 'xl'] as AvatarLabelGroupSize[]).map((size) => (
                        <AvatarLabelGroup
                            key={size}
                            size={size}
                            title="Olivia Rhye"
                            supportingText="olivia@untitledui.com"
                            src={AVATAR_PEOPLE[0].src}
                            alt={AVATAR_PEOPLE[0].alt}
                        />
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Status overlays</h3>
                <div className="flex flex-col gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['online', 'company', 'verified'] as AvatarStatus[]).map((status) => (
                        <AvatarLabelGroup
                            key={status}
                            size="md"
                            title="Olivia Rhye"
                            supportingText="olivia@untitledui.com"
                            src={AVATAR_PEOPLE[5].src}
                            alt={AVATAR_PEOPLE[5].alt}
                            status={status}
                        />
                    ))}
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={AVATAR_LABEL_GROUP_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>size</InlineCode> (sm · md · lg · xl),{' '}
                    <InlineCode>title</InlineCode>, <InlineCode>supportingText</InlineCode>, plus
                    the avatar pass-through (<InlineCode>src</InlineCode>,{' '}
                    <InlineCode>initials</InlineCode>, <InlineCode>status</InlineCode>, …).
                </p>
            </div>
        </div>
    </Section>
)

export const AvatarProfilePhotoShowcase: React.FC = () => (
    <Section
        title="AvatarProfilePhoto (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Avatar profile photo</span>{' '}
                set (ComponentSet <InlineCode>5:2302</InlineCode>). A large framed focal photo
                (white frame + subtle inner outline) with an optional verified seal.
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes</h3>
                <div className="flex flex-wrap items-end gap-spacing-4xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-3xl">
                    {(['sm', 'md', 'lg'] as AvatarProfilePhotoSize[]).map((size) => (
                        <div key={size} className="flex flex-col items-center gap-spacing-md">
                            <AvatarProfilePhoto
                                size={size}
                                src={AVATAR_PEOPLE[4].src}
                                alt={AVATAR_PEOPLE[4].alt}
                                verified
                            />
                            <span className="text-xs font-medium uppercase text-text-tertiary">
                                {size}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Content fallback</h3>
                <div className="flex flex-wrap items-end gap-spacing-4xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-3xl">
                    <div className="flex flex-col items-center gap-spacing-md">
                        <AvatarProfilePhoto
                            size="md"
                            src={AVATAR_PEOPLE[1].src}
                            alt={AVATAR_PEOPLE[1].alt}
                        />
                        <span className="text-xs font-medium uppercase text-text-tertiary">
                            image
                        </span>
                    </div>
                    <div className="flex flex-col items-center gap-spacing-md">
                        <AvatarProfilePhoto size="md" initials="OR" />
                        <span className="text-xs font-medium uppercase text-text-tertiary">
                            initials
                        </span>
                    </div>
                    <div className="flex flex-col items-center gap-spacing-md">
                        <AvatarProfilePhoto size="md" />
                        <span className="text-xs font-medium uppercase text-text-tertiary">
                            placeholder
                        </span>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={AVATAR_PROFILE_PHOTO_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>size</InlineCode> (sm · md · lg),{' '}
                    <InlineCode>src</InlineCode> / <InlineCode>alt</InlineCode>,{' '}
                    <InlineCode>initials</InlineCode>, and <InlineCode>verified</InlineCode>.
                    Mirrors Avatar's fallback chain.
                </p>
            </div>
        </div>
    </Section>
)
